/**
 * Sector operations — Member Phone Delivery UAT Section.
 * R39 E2.b (SO-P269, SO-P54, mockup m-del-uat).
 */

import React, { useEffect, useMemo, useState } from 'react'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import { Button } from '../../Common'
import { useNls } from '../../../nls/nlsProvider'
import {
  subscribeUatRounds,
  subscribeChangeRequests,
} from '../../../services/opsDeliveryChangeRequestsService'
import type {
  OpsChangeRequest,
  OpsUatItemResult,
  OpsUatRound,
} from '../../../types/ops'
import './delivery.css'

export interface MemberDeliveryUatPhoneSectionProps {
  workspaceId: string
  isAdmin?: boolean
}

export const MemberDeliveryUatPhoneSection: React.FC<MemberDeliveryUatPhoneSectionProps> = ({
  workspaceId,
}) => {
  const { t } = useNls()

  const [rounds, setRounds] = useState<OpsUatRound[]>([])
  const [changeRequests, setChangeRequests] = useState<OpsChangeRequest[]>([])
  const [reminded, setReminded] = useState(false)

  useEffect(() => {
    if (!workspaceId) return undefined
    const unsubRnd = subscribeUatRounds(workspaceId, null, (list) => setRounds(list))
    const unsubCrs = subscribeChangeRequests(workspaceId, null, (list) => setChangeRequests(list))
    return () => {
      unsubRnd()
      unsubCrs()
    }
  }, [workspaceId])

  const activeRound = useMemo(() => {
    return rounds.find((r) => r.status === 'OPEN' || r.status === 'SENT_FOR_SIGNOFF') || rounds[0] || null
  }, [rounds])

  const crStatusMap = useMemo(() => {
    const map = new Map<string, OpsChangeRequest>()
    changeRequests.forEach((cr) => map.set(cr.id, cr))
    return map
  }, [changeRequests])

  const getResultPillClass = (result: OpsUatItemResult): string => {
    switch (result) {
      case 'PASSED':
        return 'opsDeliveryPill opsDeliveryPill--good'
      case 'FAILED':
        return 'opsDeliveryPill opsDeliveryPill--bad'
      case 'BLOCKED':
        return 'opsDeliveryPill opsDeliveryPill--warn'
      case 'NOT_TESTED':
      default:
        return 'opsDeliveryPill opsDeliveryPill--neutral'
    }
  }

  const getResultLabel = (result: OpsUatItemResult): string => {
    switch (result) {
      case 'PASSED':
        return t('NLSOpsDeliveryUatItemPassed')
      case 'FAILED':
        return t('NLSOpsDeliveryUatItemFailed')
      case 'BLOCKED':
        return t('NLSOpsDeliveryUatItemBlocked')
      case 'NOT_TESTED':
      default:
        return t('NLSOpsDeliveryUatItemNotTested')
    }
  }

  const handleRemindClient = () => {
    setReminded(true)
  }

  return (
    <div
      className="opsDeliveryPhoneSection"
      data-testid="member-delivery-uat-phone-section"
    >
      <div className="opsDeliveryHeader">
        <h3 className="opsDeliveryCardTitle">
          {t('NLSOpsDeliveryPhoneUatTitle')}
          {activeRound ? ` · ${activeRound.release}` : ''}
        </h3>
        {activeRound && (
          <p className="opsDeliveryHeaderSubtitle">
            {t('NLSOpsDeliveryUatRoundTitle', {
              number: String(activeRound.roundNumber),
              version: String(activeRound.checklistVersion),
            })}
          </p>
        )}
      </div>

      <div className="opsDeliveryList" data-testid="phone-uat-items-list">
        {!activeRound || activeRound.items.length === 0 ? (
          <div className="opsDeliveryEmptyState">
            <span className="opsDeliveryKpiDesc">{t('NLSOpsDeliveryUatEmpty')}</span>
          </div>
        ) : (
          activeRound.items.map((item) => {
            const linkedCr = item.crId ? crStatusMap.get(item.crId) : undefined
            const isCrUnapproved = linkedCr && linkedCr.status !== 'APPROVED'

            return (
              <div
                key={item.id}
                className="opsDeliveryPhoneCard"
                data-testid={`phone-uat-item-${item.id}`}
              >
                <div className="opsDeliveryPhoneRow">
                  <b>{item.text}</b>
                  <span className={getResultPillClass(item.result)}>
                    {getResultLabel(item.result)}
                  </span>
                </div>

                {isCrUnapproved && (
                  <span className="opsDeliveryPill opsDeliveryPill--warn">
                    {t('NLSOpsDeliveryUatItemWaitingCr')}
                  </span>
                )}

                {item.note && <div className="opsDeliveryKpiDesc">{item.note}</div>}
              </div>
            )
          })
        )}
      </div>

      {activeRound && (
        <Button
          variant="primary"
          disabled={reminded}
          onClick={handleRemindClient}
          data-testid="phone-remind-client-btn"
        >
          <SendRoundedIcon />
          <span>
            {reminded ? t('NLSOpsDeliveryExportSuccess') : t('NLSOpsDeliveryPhoneRemindBtn')}
          </span>
        </Button>
      )}
    </div>
  )
}
